/**
 * 사내연락처 탭의 행 상세 — 직원 한 명을 보여주는 <b>조회 전용</b> 모달.
 *
 * 왜 ContactEditDialog 를 재사용하지 않는가
 *   그쪽은 '공유 범위(개인/팀/공용)' 선택과 회사·회사번호·메모 칸을 가진 연락처 편집 폼이다.
 *   직원에게는 공유 범위라는 개념이 없어서, 재사용하면 맨 위에 "개인 (나만 봄)" 같은
 *   사실이 아닌 값이 뜬다. 대신 항목만 직원에 맞게 바꾼 같은 톤의 모달을 따로 둔다
 *   (FormModal + readOnly TextField — '연락처 보기' 와 시각적으로 동일하다).
 *
 * 왜 입력칸 모양으로 보여주는가
 *   disabled 가 아니라 readOnly 를 쓴다. disabled 는 글자까지 흐려져 정작 눈으로 읽는 게
 *   목적인 화면에서 값이 잘 안 읽힌다 — ContactEditDialog 와 같은 판단이다.
 */
import React from "react";
import { Box, Stack, TextField, Typography } from "@mui/material";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";

import FormModal    from "../../../../component/common/FormModal";
import ActionButton from "../../../../component/common/ActionButton";
import { formatPhone } from "../../../../util/phone";

function SectionHeader({ title, subtitle }) {
    return (
        <Box sx={{ mb: 1.2, pb: 0.6, borderBottom: "1px solid #eef2f6" }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#0f172a", letterSpacing: 0.2 }}>
                {title}
            </Typography>
            {subtitle && (
                <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.2 }}>
                    {subtitle}
                </Typography>
            )}
        </Box>
    );
}

/** 값이 없는 항목도 칸은 남긴다 — 칸이 사라지면 사람마다 모달 모양이 달라져 읽는 자리가 흔들린다. */
function ReadField({ label, value, ...rest }) {
    return (
        <TextField
            fullWidth size="small" label={label}
            value={value || "—"}
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
            {...rest}
        />
    );
}

/**
 * @param member { id, employeeCode, name, email, phone, department, position, duty } | null
 * @param onComposeMail 이 주소로 메일 작성 콜백. 넘기면 푸터에 버튼이 생긴다.
 */
export default function CompanyMemberDialog({ open, member, onClose, onComposeMail }) {
    const m = member || {};
    const email = (m.email || "").trim();

    return (
        <FormModal
            open={open}
            onClose={onClose}
            title="직원 정보"
            subtitle="사내 명부의 정보입니다. 수정은 인사 담당자에게 요청하세요."
            maxWidth="sm"
            // onSave 를 넘기지 않으면 FormModal 이 저장 버튼을 그리지 않는다.
            cancelLabel="닫기"
            footerExtras={(onComposeMail && email) ? (
                <ActionButton
                    variant="secondary"
                    startIcon={<MailOutlineRoundedIcon sx={{ fontSize: 16 }} />}
                    onClick={() => onComposeMail({ name: m.name, email })}
                    label="이 주소로 메일 작성"
                    tooltip={`${email} 로 새 메일을 씁니다`}
                />
            ) : null}
        >
            <Stack spacing={2.5}>
                <Box>
                    <SectionHeader title="기본 정보" />
                    <Stack spacing={1.5}>
                        <Box sx={{ display: "flex", gap: 1.5 }}>
                            <ReadField label="이름" value={m.name} />
                            <ReadField label="사번" value={m.employeeCode} />
                        </Box>
                        <ReadField label="이메일" value={email} />
                    </Stack>
                </Box>

                <Box>
                    <SectionHeader title="소속" subtitle="직위는 대표이사·과장 등, 직책은 팀장·PD 등 별개의 축입니다." />
                    <Stack spacing={1.5}>
                        <ReadField label="부서" value={m.department} />
                        <Box sx={{ display: "flex", gap: 1.5 }}>
                            <ReadField label="직위" value={m.position} />
                            <ReadField label="직책" value={m.duty} />
                        </Box>
                    </Stack>
                </Box>

                <Box>
                    <SectionHeader title="연락 정보" />
                    <ReadField label="휴대폰" value={m.phone ? formatPhone(m.phone) : ""} />
                </Box>
            </Stack>
        </FormModal>
    );
}
